<!-- @format -->

<template>
	<div class="pcp-my-systems">
		<template v-for="layer in layers" :key="layer.value">
			<div v-if="layer.children && layer.children.length > 0" class="pcp-my-systems-layer">
				<div class="layer-title">{{ layer.label }}</div>
				<div class="layer-content">
					<template v-for="item in layer.children" :key="item.id">
						<el-tooltip :content="item.name" placement="top" :disabled="!overflowMap[item.id]">
							<div
								class="system-item"
								:class="{ active: globalStore.curSystemAlias === item.alias }"
								@click="() => toSubSystem(item)"
							>
								<img v-if="item.logo" :src="item.logo" class="system-icon" />
								<span :ref="(el) => setNameRef(el, item.id)" class="system-name">
									{{ item.name }}
								</span>
							</div>
						</el-tooltip>
					</template>
				</div>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
	import { useGlobalStoreWithOut } from '@power/runtime-vue3';
	import { toSubSystem } from './utils';
	import { getDicts } from '@/api/dict/data';

	const globalStore = useGlobalStoreWithOut();

	const layers = ref<any>([]);
	// 记录每个名称是否溢出（使用 ref，模板中可直接访问）
	const overflowMap = ref<Record<string, boolean>>({});
	// 保存每个名称元素的引用，便于批量重算；同时为每个元素挂载 ResizeObserver
	const nameRefs = new Map<string, HTMLElement>();
	const nameObservers = new Map<string, ResizeObserver>();
	const setNameRef = (el: HTMLElement | null, id: string) => {
		if (id === undefined || id === null) return;
		const key = String(id);
		// 解绑旧元素的观察
		if (!el) {
			const ob = nameObservers.get(key);
			if (ob) {
				ob.disconnect();
				nameObservers.delete(key);
			}
			nameRefs.delete(key);
			delete overflowMap.value[key];
			return;
		}
		// 绑定新元素
		nameRefs.set(key, el);
		// 在下一帧计算，确保布局完成
		requestAnimationFrame(() => computeOverflowFor(key, el));
		// 观察尺寸变化，动态重算
		const ro = new ResizeObserver(() => computeOverflowFor(key, el));
		ro.observe(el);
		nameObservers.set(key, ro);
	};
	const computeOverflowFor = (id: string, el: HTMLElement) => {
		// 两次 rAF 以应对异步字体/图片导致的布局抖动
		requestAnimationFrame(() => {
			const isOverflow = el.scrollWidth > el.clientWidth;
			if (overflowMap.value[String(id)] !== isOverflow) {
				overflowMap.value[String(id)] = isOverflow;
			}
		});
	};
	const computeAllOverflow = () => {
		nameRefs.forEach((el, id) => computeOverflowFor(String(id), el));
	};

	const initData = () => {
		getDicts('zxtfl').then((res) => {
			const mapped = res.map((item: any) => {
				const children: any[] = [];
				globalStore.subSystems.forEach((subSystem: any) => {
					if (item.value === subSystem.category) {
						children.push(subSystem);
					}
				});
				return {
					...item,
					children,
				};
			});
			// 追加“其它”分组：category 为空的子系统归入该分组
			const others = globalStore.subSystems.filter(
				(subSystem: any) => !subSystem.category || String(subSystem.category).trim() === '',
			);
			layers.value = others.length
				? [
						...mapped,
						{
							label: '其它',
							value: 'other',
							children: others,
						},
				  ]
				: mapped;
			// 下一帧计算溢出状态
			nextTick(() => computeAllOverflow());
		});
	};
	initData();

	// 当层级或子系统变化后，重新计算
	watch(
		() => layers.value,
		() => nextTick(() => computeAllOverflow()),
		{ deep: true },
	);
	watch(
		() => globalStore.subSystems,
		() => nextTick(() => computeAllOverflow()),
		{ deep: true },
	);
	onMounted(() => {
		window.addEventListener('resize', computeAllOverflow);
		nextTick(() => computeAllOverflow());
	});
	onBeforeUnmount(() => {
		window.removeEventListener('resize', computeAllOverflow);
		// 断开所有观察者
		nameObservers.forEach((ob) => ob.disconnect());
		nameObservers.clear();
	});
</script>

<style scoped lang="scss">
	.pcp-my-systems {
		overflow-y: auto;
		padding: 20px;
		background: #fff;
		border-radius: 8px;

		.pcp-my-systems-layer {
			margin-bottom: 20px;

			.layer-title {
				position: relative;
				font-size: 16px;
				font-weight: bold;
				color: #333;
				margin-bottom: 15px;
				padding-left: 12px;
				font-family: AlibabaPuHuiTi;
				line-height: 22px;

				&::before {
					content: '';
					position: absolute;
					left: 0;
					top: 50%;
					transform: translateY(-50%);
					width: 4px;
					height: 16px;
					background-color: var(--el-color-primary);
					border-radius: 2px;
				}
			}

			.layer-content {
				display: grid;
				grid-template-columns: repeat(4, 1fr);
				gap: 10px;
			}

			.system-item {
				display: flex;
				align-items: center;
				padding: 8px 16px;
				background: #fff;
				border: 1px solid #efefef;
				border-radius: 8px;
				cursor: pointer;
				min-width: 120px;
				height: 50px;

				&:hover {
					border-color: var(--el-color-primary);
					box-shadow: 0 2px 4px rgba(29, 89, 242, 0.1);

					.system-name {
						color: var(--el-color-primary);
					}
				}

				&.active {
					border-color: var(--el-color-primary);
					border-width: 1px;
					color: var(--el-color-primary);

					.system-name {
						color: var(--el-color-primary);
						font-weight: bold;
					}
				}

				.system-icon {
					width: 30px;
					height: 30px;
					margin-right: 8px;
					flex-shrink: 0;
				}

				.system-name {
					font-size: 14px;
					color: #333;
					white-space: nowrap;
					overflow: hidden;
					text-overflow: ellipsis;
					font-weight: 400;
					flex: 1;
					min-width: 0;
					line-height: 20px;
					text-align: left;
					font-family: AlibabaPuHuiTi;
				}
			}
		}
	}
</style>
